import React from 'react';
import { StepProps, SPECIALTY_COLORS } from '../../types';
import { motion } from 'framer-motion';
import { RotateCcw, Search, Share2, Target } from 'lucide-react';
export function Summary({
  state,
  goToStep,
  resetApp
}: StepProps) {
  const role = state.selectedSpecialty || 'Nephrologist';
  const color = SPECIALTY_COLORS[role];
  // Calculate perception accuracy if they used perception mode
  let accuracyScore = 0;
  if (state.perceptionMode) {
    const total = 4;
    let correct = 0;
    if (state.perceptionNeph === 'Late / Cautious') correct++;
    if (state.perceptionCardio === 'Early / Proactive') correct++;
    if (state.perceptionEndo === 'Balanced') correct++;
    if (state.perceptionPCP === 'Late / Cautious') correct++;
    accuracyScore = Math.round(correct / total * 100);
  }
  const roleSummary = {
    Nephrologist: 'Safety-focused and cautious, requiring clear sequencing guidance.',
    Cardiologist: 'Proactive and outcome-driven, looking for dual-organ protection.',
    Endocrinologist: 'Data-driven but metabolically focused, hesitant on non-glycaemic escalation.',
    PCP: 'Conservative and guideline-dependent, needing simple escalation pathways.'
  }[role];
  return <div className="flex-grow flex flex-col py-12 px-6 overflow-y-auto bg-dark text-white" data-id="element-457">
      <div className="max-w-4xl w-full mx-auto" data-id="element-458">
        <div className="text-center mb-12" data-id="element-459">
          <Target size={48} className="mx-auto mb-6 text-brand" data-id="element-460" />
          <h2 className="text-3xl md:text-5xl font-bold mb-6" data-id="element-461">
            Understanding how each specialty thinks is critical to improving
            engagement and treatment outcomes.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12" data-id="element-462">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="bg-white/10 rounded-3xl p-8 border border-white/20" data-id="element-463">
            <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-4" data-id="element-464">
              You Explored
            </h3>
            <div className="flex items-center mb-4" data-id="element-465">
              <div className="w-4 h-4 rounded-full mr-3" style={{
              backgroundColor: color
            }} data-id="element-466"></div>
              <span className="text-2xl font-bold" data-id="element-467">{role}s</span>
            </div>
            <p className="text-gray-300 text-lg leading-relaxed" data-id="element-468">
              {roleSummary}
            </p>
          </motion.div>

          {state.perceptionMode && <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} className="bg-white/10 rounded-3xl p-8 border border-white/20 flex flex-col justify-center items-center text-center" data-id="element-469">
              <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-2" data-id="element-470">
                Your Perception Accuracy
              </h3>
              <div className="text-5xl font-black text-brand mb-2" data-id="element-471">
                {accuracyScore}%
              </div>
              <p className="text-gray-300" data-id="element-472">
                {accuracyScore >= 75 ? 'Strong alignment with market reality.' : 'Opportunity to recalibrate assumptions.'}
              </p>
            </motion.div>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pb-12" data-id="element-473">
          <motion.button initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} onClick={() => goToStep(4)} className="bg-white text-dark rounded-2xl p-6 font-bold text-lg hover:bg-gray-100 transition-colors flex flex-col items-center justify-center gap-3" data-id="element-474">
            <Search size={24} className="text-brand" data-id="element-475" />
            Explore Another Specialty
          </motion.button>

          <motion.button initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} onClick={resetApp} className="bg-white/10 text-white border border-white/20 rounded-2xl p-6 font-bold text-lg hover:bg-white/20 transition-colors flex flex-col items-center justify-center gap-3" data-id="element-476">
            <RotateCcw size={24} data-id="element-477" />
            Restart from Beginning
          </motion.button>

          <motion.button initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.4
        }} disabled className="bg-white/5 text-white/50 border border-white/10 rounded-2xl p-6 font-bold text-lg cursor-not-allowed flex flex-col items-center justify-center gap-3" data-id="element-478">
            <Share2 size={24} data-id="element-479" />
            Share Insights
          </motion.button>
        </div>
      </div>
    </div>;
}